import { Component, h } from 'preact'
import Panel from '../panel'
import InputGroup from '../ui-components/InputGroup'
import axios from 'axios'
import TimezoneSelector, { TimezoneList } from './TimezoneSelector'
import AlertComponent from '../ui-components/AlertComponent'
import Header from '../header'
import Switcher from '../control/Switcher'
import classNames from 'classnames'

type configItem = {
  activityName: string,
  points: number
}

interface Activity {
    activityName: string,
    points: number,
    enabled?: boolean,
    error?: string
}

interface State {
  activityList: Array<Activity>,
  selectedTimezone: string,
  timezoneList: TimezoneList,
  isSaveButtonLoading: boolean,
  messageType?: string|null,
  message?: string
}

interface Props {
    csrfToken: string
}

class PointSystemConfiguration extends Component<Props, State> {
  configurableActivityList: Array<Activity> = [
    {
      activityName: 'provided answer',
      points: 0
    },
    {
      activityName: 'replied topic',
      points: 0
    },
    {
      activityName: 'received like',
      points: 0
    },
    {
      activityName: 'given like',
      points: 0
    },
    {
      activityName: 'created topic',
      points: 0
    }]

  constructor (props: Props) {
    super(props)
    this.state = {
      activityList: [],
      selectedTimezone: '',
      timezoneList: [],
      messageType: null,
      message: '',
      isSaveButtonLoading: false
    }
    this.onTimezoneSelected = this.onTimezoneSelected.bind(this)
    this.combineActivityList = this.combineActivityList.bind(this)
  }

  onTimezoneSelected (timezoneValue: string) {
    this.setState({ selectedTimezone: timezoneValue })
  }

  combineActivityList (configuration: Array<configItem>, customerConfiguration: Array<configItem>): Array<configItem> {
    let defaultConfigurationObject: {
      [key: string]: number
    } = {}
    configuration.forEach((configurationItem: configItem) => {
      defaultConfigurationObject[configurationItem.activityName] = configurationItem.points
    })

    let customerConfigurationObject: {
      [key: string]: number
    } = {}
    customerConfiguration.forEach((configurationItem: configItem) => {
      customerConfigurationObject[configurationItem.activityName] = configurationItem.points
    })

    const combinedConfigurationObject = Object.assign({}, defaultConfigurationObject, customerConfigurationObject)
    return Object.keys(combinedConfigurationObject).map(combinedConfigurationItem => ({
      activityName: combinedConfigurationItem,
      points: combinedConfigurationObject[combinedConfigurationItem]
    }))
  }

  async componentDidMount () {
    this.setState({ messageType: null })

    try {
      const res = await axios.get('/settings/pointSystem/configuration')
      let configuration = this.configurableActivityList
      if (res.data) {
        if (res.data.activityList) {
          configuration = this.combineActivityList(this.configurableActivityList, res.data.activityList)
        }
        if (res.data.timezone) {
          this.setState({ selectedTimezone: res.data.timezone })
        }
        if (res.data.timezoneList) {
          this.setState({ timezoneList: res.data.timezoneList })
        }
      }

      this.setState({ activityList: configuration })
    } catch (e) {
      this.setState({ messageType: 'error', message: 'Unable to retrieve point system configuration.' })
    }
  }

  async onSave () {
    if (!this.validate()) {
      return
    }
    this.setState({ isSaveButtonLoading: true })
    try {
      await axios.post('/settings/pointSystem/configuration', {
        timezone: this.state.selectedTimezone,
        activityList: this.state.activityList
      })
    } catch (e) {
      this.setState({
        messageType: 'error',
        message: 'Failed to save configuration'
      })
    }
    this.setState({ isSaveButtonLoading: false })
  }

  validate () {
    const activityList = this.state.activityList.map(activity => Object.assign(activity, {
      error: (activity.enabled && activity.points < 1)
        ? 'Please enter a number greater than 0 to enable this rule'
        : undefined
    }))
    this.setState({ activityList: activityList })
    return typeof activityList.find(activity => activity.error) === 'undefined'
  }

  onPointsChange (points: number, i: number) {
    let activityList = this.state.activityList
    activityList[i]['points'] = Math.max(points, 0)
    activityList[i]['error'] = undefined
    this.setState({ activityList: activityList })
  }

  onActivityToggle (activity: Activity, i: number) {
    let activityList = this.state.activityList
    if (!activity.enabled && activity.points < 1) {
      activityList[i]['error'] = 'Please enter a number greater than 0 to enable this rule'
    } else {
      activityList[i]['enabled'] = !activity.enabled
      activityList[i]['error'] = undefined
    }
    this.setState({ activityList: activityList })
  }

  render (props: Props, state: State) {
    const isVisibleFor = state.messageType === 'success' ? 5000 : false
    return (
      <div className='point-system-container'>
        <Header title='Point system' saveCallback={() => this.onSave()} disableSaveButton={false} isLoading={this.state.isSaveButtonLoading} />
        <Panel title='Point system rules' class='point-system-rules'>
          {
            state.messageType ? <AlertComponent messageType={state.messageType} message={state.message} sticky renderHTML isVisibleFor={isVisibleFor} /> : null
          }
          {state.activityList.map((activity, i) =>
            <div class={classNames({
              'activity-row': true,
              'validation-error': activity.error
            })} >
              <Switcher on={activity.enabled} onChange={() => { this.onActivityToggle(activity, i) }} />
              <span class='activity-description-left'>Award </span>
              <InputGroup key={activity.activityName} error={activity.error} type='number' min={0} required qaClasses='qa-points' placeholder='0' value={activity.points} onChangeCallback={(e: ChangeEvent) => this.onPointsChange(+e.target.value, i)} />
              <span class='activity-description-right'>points for { activity.activityName }</span>
            </div>
          )}
        </Panel>
        <Panel title='Timezone setting' class='point-system-timezone-container'>
          <div>
            The weekly user leaderboard automatically resets at midnight on Sunday/Monday according to your timezone selection.
          </div>
          <div className='timezone-selector-container'>
            <label>Timezone</label>
            <TimezoneSelector
              timezones={state.timezoneList}
              selected={state.timezoneList.find((timezone) => timezone.value === state.selectedTimezone)}
              onChange={this.onTimezoneSelected}
            />
          </div>
        </Panel>
        <div class='hide'>
          <button type='submit' id='save_point_system'>Save changes</button>
        </div>
      </div>
    )
  }
}

export default PointSystemConfiguration
